import { EmptyState, Skeleton } from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'

import { useCurrentPermissionsState, useMemberships } from '@/api/queries'
import { useCurrentAdmin } from '@/auth/use-session'
import { PageHeader } from '@/components/layout/app-shell'
import { useSelectedOrganizationId } from '@/org/use-organization'

import { DeskDay, MyDay, OwnerHome } from '@/features/dashboard/gym-day'
import { pickHome } from '@/features/dashboard/home-pick'
import { PlatformHome } from '@/features/dashboard/platform-home'

/**
 * La home — ANL-09.
 *
 * Non è una pagina, sono **quattro** pagine e la scelta di quale mostrare: la regola sta
 * in `home-pick.ts` (dove si prova senza React) e il contenuto in un componente per vista.
 * Questo file è solo il bivio, ed è tenuto piccolo per una ragione: il giorno in cui si
 * aggiunge una quinta vista, la modifica è una riga qui e un file là.
 *
 * Il titolo cambia con la vista e non è un dettaglio di stile: «Dashboard» su una
 * schermata che elenca gli appuntamenti di oggi non dice a chi la guarda che sta vedendo i
 * *propri* — e la home è l'unica pagina del pannello di cui non si è scelto il nome
 * cliccando.
 *
 * Finché le appartenenze non sono arrivate non si sceglie: mostrare la dashboard del
 * prodotto per un istante e poi sostituirla con quella della palestra sarebbe peggio di
 * un'attesa dichiarata, perché il primo schermo è quello che si ricorda.
 */
export const Route = createFileRoute('/_app/')({
  component: HomePage,
})

function HomePage() {
  const admin = useCurrentAdmin()
  const { permissions, isPending } = useCurrentPermissionsState()
  const memberships = useMemberships()
  const selectedId = useSelectedOrganizationId()

  if (isPending) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((tile) => (
            <Skeleton key={tile} className="h-28 w-full" />
          ))}
        </div>
      </>
    )
  }

  const kind = pickHome({ isPlatformAdmin: admin?.role === 'admin', permissions })

  // L'appartenenza con cui stiamo lavorando: da lì viene `memberId`, cioè ciò che rende
  // «la mia giornata» mia. È la stessa cascata del selettore di organizzazione —
  // selezione conservata, altrimenti quella che il server ha risolto da sé.
  const actor = memberships.data?.find((item) =>
    selectedId === null ? item.current : item.organizationId === selectedId,
  )

  if (kind === 'owner') {
    return (
      <>
        <PageHeader
          title="La palestra questo mese"
          description="Incassi, iscritti e le scadenze dei prossimi giorni. Le sei analisi complete sono in Analisi."
        />
        <OwnerHome />
      </>
    )
  }

  if (kind === 'desk') {
    return (
      <>
        <PageHeader
          title="La giornata"
          description="Chi c'è adesso, chi è passato, le lezioni e gli appuntamenti di oggi."
        />
        <DeskDay />
      </>
    )
  }

  if (kind === 'my-day') {
    return (
      <>
        <PageHeader title="La mia giornata" description="I tuoi appuntamenti e le tue lezioni." />
        <MyDay memberId={actor?.memberId} />
      </>
    )
  }

  if (kind === 'platform') {
    return (
      <>
        <PageHeader
          title="Dashboard"
          description="Stato di catalogo, utenti e Coach. I dati sono aggregati dal backend."
        />
        <PlatformHome />
      </>
    )
  }

  return (
    <>
      <PageHeader title="Dashboard" />
      <EmptyState
        title="Nessuna sintesi per il tuo ruolo"
        description="Il tuo ruolo non include i numeri della palestra né l'agenda, quindi qui non c'è niente di vero da riassumere. Le pagine che puoi usare sono nel menù."
      />
    </>
  )
}
